import { computed, inject, provide, ref } from 'vue'

const FEATURE_FLAGS_INJECTION_KEY = 'LIB_FEATURE_FLAGS'

const normalizeFlags = (flags) => (
  flags && typeof flags === 'object' && !Array.isArray(flags)
    ? flags
    : {}
)

const createFeatureFlagsApi = ({
  localFlags = ref({}),
  parentFeatureFlags = null,
  mergeWithParent = true
} = {}) => {
  const flags = computed(() => {
    const normalizedLocalFlags = normalizeFlags(localFlags.value)

    if (!mergeWithParent || !parentFeatureFlags) {
      return normalizedLocalFlags
    }

    return {
      ...normalizeFlags(parentFeatureFlags.flags?.value),
      ...normalizedLocalFlags
    }
  })

  const setFeatureFlags = (newFlags = {}) => {
    localFlags.value = {
      ...normalizeFlags(localFlags.value),
      ...normalizeFlags(newFlags)
    }
  }

  const setFeatureFlag = (flagName, flagValue = true) => {
    if (!flagName) return
    setFeatureFlags({ [flagName]: flagValue })
  }

  const getFeatureFlag = (flagName, fallbackValue = false) => {
    if (!flagName) return fallbackValue

    const flagValue = flags.value[flagName]
    return flagValue === undefined ? fallbackValue : flagValue
  }

  const isFeatureEnabled = (flagName, fallbackValue = false) =>
    !!getFeatureFlag(flagName, fallbackValue)

  return {
    flags,
    getFeatureFlag,
    isFeatureEnabled,
    setFeatureFlag,
    setFeatureFlags
  }
}

/**
 * Creates and provides a feature flags scope for the current component subtree.
 * Nested scopes can inherit flags from parent providers and override locally.
 * @param {Object} options
 * @param {Object} options.flags - Initial feature flags for the current scope.
 * @param {Boolean} options.mergeWithParent - If true, merges parent scope flags with local flags.
 * @returns {Object}
 * @returns {import('vue').ComputedRef<Object>} returns.flags - The resolved feature flags map.
 * @returns {Function} returns.getFeatureFlag - Gets a feature flag value by name.
 * @returns {Function} returns.isFeatureEnabled - Returns a boolean based on a flag value.
 * @returns {Function} returns.setFeatureFlag - Sets a single feature flag.
 * @returns {Function} returns.setFeatureFlags - Merges many feature flags at once.
 */
export const provideFeatureFlags = (options = {}) => {
  const {
    flags = {},
    mergeWithParent = true
  } = options

  const parentFeatureFlags = inject(FEATURE_FLAGS_INJECTION_KEY, null)
  const localFlags = ref(normalizeFlags(flags))

  const featureFlags = createFeatureFlagsApi({
    localFlags,
    parentFeatureFlags,
    mergeWithParent
  })

  provide(FEATURE_FLAGS_INJECTION_KEY, featureFlags)

  return featureFlags
}

/**
 * Reads feature flags from the nearest provider.
 * If no provider is found, returns a local fallback flags API.
 * @param {Object} options
 * @param {Object} options.fallbackFlags - Flags used when no provider exists.
 * @returns {Object}
 * @returns {import('vue').ComputedRef<Object>} returns.flags - The resolved feature flags map.
 * @returns {Function} returns.getFeatureFlag - Gets a feature flag value by name.
 * @returns {Function} returns.isFeatureEnabled - Returns a boolean based on a flag value.
 * @returns {Function} returns.setFeatureFlag - Sets a single feature flag.
 * @returns {Function} returns.setFeatureFlags - Merges many feature flags at once.
 */
export default function useFeatureFlags (options = {}) {
  const { fallbackFlags = {} } = options
  const providedFeatureFlags = inject(FEATURE_FLAGS_INJECTION_KEY, null)

  if (providedFeatureFlags) {
    return providedFeatureFlags
  }

  return createFeatureFlagsApi({
    localFlags: ref(normalizeFlags(fallbackFlags)),
    mergeWithParent: false
  })
}
